import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, X } from "lucide-react";
import { API_URL } from "../../config";

export default function SeriesTab({ token }) {
  const auth = { Authorization: `Bearer ${token}` };
  const jsonAuth = { "Content-Type": "application/json", ...auth };
  const [list, setList] = useState(null);
  const [msg, setMsg] = useState("");
  const [auto, setAuto] = useState(null);                 // результат предпросмотра автосборки
  const [form, setForm] = useState({ title: "", description: "", q: "" });
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    fetch(`${API_URL}/admin/series`, { headers: auth }).then(r => (r.ok ? r.json() : [])).then(setList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(id => {
    fetch(`${API_URL}/admin/series/${id}`, { headers: auth }).then(r => (r.ok ? r.json() : null)).then(setDetail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { if (openId) loadDetail(openId); else setDetail(null); }, [openId, loadDetail]);

  const refresh = () => { load(); if (openId) loadDetail(openId); };

  const previewAuto = async () => {
    const r = await fetch(`${API_URL}/admin/series/build-auto`, { method: "POST", headers: jsonAuth, body: JSON.stringify({ dry_run: true }) });
    setAuto(r.ok ? await r.json() : null);
    if (!r.ok) setMsg("Не удалось посчитать");
  };
  const runAuto = async () => {
    const r = await fetch(`${API_URL}/admin/series/build-auto`, { method: "POST", headers: jsonAuth, body: JSON.stringify({ dry_run: false }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || "Не удалось собрать"); return; }
    const created = d.series.filter(s => s.created).length;
    const added = d.series.reduce((a, s) => a + s.added, 0);
    setMsg(`Готово: создано серий ${created}, добавлено материалов ${added}.`);
    setAuto(null); refresh();
  };

  const create = async e => {
    e.preventDefault();
    const r = await fetch(`${API_URL}/admin/series`, { method: "POST", headers: jsonAuth, body: JSON.stringify({ title: form.title, description: form.description }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || "Не удалось создать"); return; }
    if (form.q.trim().length >= 3) {
      const f = await fetch(`${API_URL}/admin/series/${d.id}/fill`, { method: "POST", headers: jsonAuth, body: JSON.stringify({ q: form.q.trim() }) });
      const fd = await f.json().catch(() => ({}));
      setMsg(`Серия создана. По запросу «${form.q.trim()}» добавлено материалов: ${fd.added ?? 0}.`);
    } else setMsg("Серия создана. Добавьте материалы кнопкой «Добавить по запросу».");
    setForm({ title: "", description: "", q: "" });
    refresh();
  };

  const patch = async (s, body) => {
    const r = await fetch(`${API_URL}/admin/series/${s.id}`, { method: "PATCH", headers: jsonAuth, body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) setMsg(d.detail || "Не удалось сохранить");
    refresh();
  };
  const rename = s => {
    const title = window.prompt("Название серии:", s.title);
    if (title === null || !title.trim() || title.trim() === s.title) return;
    const description = window.prompt("Описание (можно оставить пустым):", s.description || "");
    patch(s, { title: title.trim(), ...(description !== null ? { description } : {}) });
  };
  const fill = async s => {
    const q = window.prompt("Добавить в серию все материалы, где в названии или описании есть:", "");
    if (!q || q.trim().length < 3) return;
    const r = await fetch(`${API_URL}/admin/series/${s.id}/fill`, { method: "POST", headers: jsonAuth, body: JSON.stringify({ q: q.trim() }) });
    const d = await r.json().catch(() => ({}));
    setMsg(r.ok ? `Найдено ${d.matched}, добавлено ${d.added}.` : (d.detail || "Не удалось добавить"));
    refresh();
  };
  const remove = async s => {
    if (!window.confirm(`Удалить серию «${s.title}»? Сами материалы останутся на сайте.`)) return;
    await fetch(`${API_URL}/admin/series/${s.id}`, { method: "DELETE", headers: auth });
    if (openId === s.id) setOpenId(null);
    refresh();
  };
  const removeItem = async (sid, lid) => {
    await fetch(`${API_URL}/admin/series/${sid}/items/${lid}`, { method: "DELETE", headers: auth });
    refresh();
  };
  const move = async (items, index, dir) => {
    const ids = items.map(i => i.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    await fetch(`${API_URL}/admin/series/${openId}/order`, { method: "PUT", headers: jsonAuth, body: JSON.stringify({ lecture_ids: ids }) });
    loadDetail(openId);
  };

  return (
    <div>
      <p style={{ color: "#888", fontSize: "0.9rem", marginTop: 0 }}>
        Серии — тематические подборки видео на странице «Материалы». Один материал может входить в несколько серий.
        Серии без материалов и скрытые посетителям не показываются.
      </p>

      <div className="card" style={{ marginBottom: 16 }}>
        <strong>Автосборка по темам</strong>
        <div style={{ color: "#888", fontSize: "0.85rem", margin: "4px 0 10px" }}>
          По ключевым словам в названиях создаются серии: стимуляция и ресинхронизация, тахикардии, блокады, холтер и анализ ЭКГ,
          картирование, ИИ и цифровая медицина, клинические разборы, математика. Повторный запуск добавляет только новые материалы
          и ничего не удаляет.
        </div>
        <button type="button" onClick={previewAuto} style={{ padding: "5px 14px", background: "var(--gray-500)" }}>Предпросмотр</button>
        {auto && (
          <div style={{ marginTop: 12, padding: 12, background: "#fff1f6", border: "1.5px solid #ffc2d9", borderRadius: 8 }}>
            <table className="admin-table"><tbody>
              {auto.series.map(s => <tr key={s.title}><td>{s.title}</td><td style={{ width: 120 }}>подходит: <strong>{s.matched}</strong></td></tr>)}
            </tbody></table>
            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
              <button type="button" onClick={runAuto} style={{ padding: "5px 14px" }}>Создать / дополнить серии</button>
              <button type="button" onClick={() => setAuto(null)} style={{ padding: "5px 14px", background: "var(--gray-500)" }}>Отмена</button>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={create} className="card" style={{ marginBottom: 16 }}>
        <strong>Новая серия</strong>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }} className="admin-form-grid-2">
          <input className="admin-input" placeholder="Название *" value={form.title} required maxLength={200} onChange={e => setForm({ ...form, title: e.target.value })} />
          <input className="admin-input" placeholder="Наполнить по запросу (необязательно), например: картирование" value={form.q} onChange={e => setForm({ ...form, q: e.target.value })} />
        </div>
        <textarea className="admin-textarea" rows={2} placeholder="Описание — 1–2 предложения, о чём серия" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ marginTop: 10 }} />
        <button type="submit" style={{ marginTop: 10 }}>Создать серию</button>
      </form>

      {msg && <div className="card" style={{ marginBottom: 12, background: "#fff1f6", border: "1.5px solid #ffc2d9", color: "#9c0040" }}>{msg}</div>}

      {list === null ? <p>Загрузка…</p> : list.length === 0 ? <p style={{ color: "#888" }}>Серий пока нет.</p> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {list.map(s => (
            <div key={s.id} className="card">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <strong>{s.title}</strong> <span style={{ color: "#888", fontSize: "0.85rem" }}>· {s.count} мат. {s.is_published ? "" : "· скрыта"}</span>
                  {s.description && <div style={{ color: "#888", fontSize: "0.85rem", marginTop: 2 }}>{s.description}</div>}
                </div>
                <button type="button" onClick={() => setOpenId(openId === s.id ? null : s.id)} style={{ padding: "4px 10px", background: "var(--gray-500)" }}>{openId === s.id ? "Свернуть" : "Состав"}</button>
                <button type="button" onClick={() => fill(s)} style={{ padding: "4px 10px" }}>Добавить по запросу</button>
                <button type="button" onClick={() => rename(s)} style={{ padding: "4px 10px", background: "var(--gray-500)" }}>Изменить</button>
                <button type="button" onClick={() => patch(s, { is_published: !s.is_published })} style={{ padding: "4px 10px", background: "var(--gray-500)" }}>{s.is_published ? "Скрыть" : "Показать"}</button>
                <Link to={`/series/${s.id}`} style={{ fontSize: "0.85rem" }}>открыть →</Link>
                <button type="button" onClick={() => remove(s)} style={{ padding: "4px 10px", background: "#c0392b" }}>Удалить</button>
              </div>
              {openId === s.id && detail && (
                <ol style={{ margin: "12px 0 0", paddingLeft: 22, display: "flex", flexDirection: "column", gap: 4 }}>
                  {detail.items.map((l, i) => (
                    <li key={l.id} style={{ fontSize: "0.9rem" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {l.title}
                        <button type="button" aria-label="Выше" onClick={() => move(detail.items, i, -1)} style={{ padding: 2, background: "none", color: "var(--gray-600)", border: "none" }}><ArrowUp size={14} /></button>
                        <button type="button" aria-label="Ниже" onClick={() => move(detail.items, i, 1)} style={{ padding: 2, background: "none", color: "var(--gray-600)", border: "none" }}><ArrowDown size={14} /></button>
                        <button type="button" aria-label="Убрать из серии" onClick={() => removeItem(s.id, l.id)} style={{ padding: 2, background: "none", color: "#c0392b", border: "none" }}><X size={14} /></button>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
